// Lead time and stock messaging, shared by the product form and the cart. Every
// message is composed from live variant data at render time — a back in stock
// date has to name the date it is still due on, not the one it was due on when
// the line was added — so nothing here is ever snapshotted.

const ordinalSuffix = (day) => {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const parseMetafieldDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatShipDate = (date) => {
  const locale = window.Shopify?.locale || "en";
  const month = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  const day = date.getDate();
  return `${month} ${day}${ordinalSuffix(day)}`;
};

const defaultStrings = () => window.localeStrings.product.stock_message;

/**
 * The stock message a variant carries, if any.
 *
 * @param {object} variant { customColor, currentlyNotInStock, backInStockDate,
 *        leadTime } — as returned by the Storefront API, or rebuilt from the
 *        equivalent Liquid metafields
 * @param {object} strings locales product.stock_message
 * @returns {string|null}
 */
export function stockMessage(variant, strings = defaultStrings()) {
  if (!variant) return null;

  // Made-to-order items always surface their lead time, regardless of stock.
  if (variant.customColor === "true") return strings.made_to_order;

  // Variant is in stock
  if (!variant.currentlyNotInStock) return null;

  // Known back-in-stock date in the future → show the exact ship date.
  const backInStockDate = parseMetafieldDate(variant.backInStockDate);
  if (backInStockDate && backInStockDate > new Date()) {
    return strings.back_in_stock_date.replace(
      "[date]",
      formatShipDate(backInStockDate),
    );
  }

  // No date, but the variant specifies a lead time → show a timeline.
  if (variant.leadTime) {
    return strings.back_in_stock_timeline.replace(
      "[timeline]",
      variant.leadTime,
    );
  }

  // Nothing known → generic restock message.
  return strings.back_in_stock;
}

/**
 * The lead time a configured monogram adds, driven by its selected placement:
 * a placement's own `lead_time_override` wins, then `extended_lead_time` swaps
 * in the longer global message; otherwise the default global message applies.
 *
 * Unlike the variant messages this one is fixed for the life of the line — the
 * placement can't change once it's in the cart — so it is saved to the monogram
 * line as `_monogram_lead_time` and read back from there.
 *
 * @param {object} monogram configured monogram, as { data, variant, selection,
 *        placement } — null when none is configured
 * @param {object} strings  locales product.stock_message
 * @returns {string|null}
 */
export function monogramLeadTime(monogram, strings = defaultStrings()) {
  if (!monogram) return null;

  const placement = monogram.placement;
  if (placement?.leadTimeOverride) return placement.leadTimeOverride;

  const extended =
    placement?.extendedLeadTime === "true" ||
    placement?.extendedLeadTime === true;

  return extended
    ? strings.monogram_extended_lead_time
    : strings.monogram_lead_time;
}
